// assets/nachfuellset-konfigurator.js
// Nachfüllset-Konfigurator: Menge = echte Variante (Option 1), Duft = Line-Item-Property.
// Beim 2er-Pack sind zwei Düfte frei mischbar. Keine Gravur.
// Reine Funktionen sind DOM-frei und werden mit node --test geprüft.

// Findet die Variante mit exakt gleicher Options-Sequenz oder null.
export function selectVariant(variants, selection) {
  return variants.find(v =>
    v.options.length === selection.length &&
    v.options.every((opt, i) => opt === selection[i])
  ) ?? null;
}

// Formatiert Cent-Beträge deutsch: 1799 => "17,99 €".
export function formatEuro(cents) {
  return (cents / 100).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}

export function buildPriceState(price, compareAt) {
  const current = Number(price) || 0;
  const compareValue = Number(compareAt) || 0;
  const onSale = compareValue > current;
  return {
    current: formatEuro(current),
    compare: onSale ? formatEuro(compareValue) : "",
    percent: onSale ? Math.round(((compareValue - current) * 100) / compareValue) : 0,
    onSale
  };
}

// Nächstliegender Slide-Index aus der Track-Scrollposition, geklemmt auf [0, count-1].
export function activeSlideIndex(scrollLeft, slideWidth, count) {
  const total = Number.isInteger(count) && count > 0 ? count : 0;
  if (total === 0) return 0;
  const w = Number(slideWidth);
  if (!Number.isFinite(w) || w <= 0) return 0;
  const raw = Math.round(Number(scrollLeft) / w);
  return Math.min(total - 1, Math.max(0, Number.isFinite(raw) ? raw : 0));
}

// Baut die Duft-Line-Item-Properties. 1er => { "Duft": d1 }, 2er => { "Duft 1": d1, "Duft 2": d2 }.
// Leere Werte werden ausgelassen (kein leerer Property-Eintrag im Warenkorb).
export function buildDuftProperties(isDouble, duft1, duft2) {
  const props = {};
  if (isDouble) {
    if (duft1) props["Duft 1"] = duft1;
    if (duft2) props["Duft 2"] = duft2;
  } else {
    if (duft1) props["Duft"] = duft1;
  }
  return props;
}

// ── DOM-Wiring ──────────────────────────────────────────────────────────────
function initNachfuellset(root) {
  const variantsEl = root.querySelector("[data-variants]");
  if (!variantsEl) return;
  const variants = JSON.parse(variantsEl.textContent)
    .map(v => ({ id: v.id, options: v.options, available: v.available, price: v.price, compareAt: v.compare_at_price }));
  if (!variants.length) return;

  const doubleValue = root.dataset.doubleValue || "";
  const idField = root.querySelector("[data-variant-id]");
  const addBtn = root.querySelector("[data-add-to-cart]");
  const priceDisplay = root.querySelector("[data-price-display]");

  // Zweiter-Duft-Block + versteckte Property-Inputs.
  const duftGroup2 = root.querySelector("[data-duft-group-2]");
  const propDuft = root.querySelector("[data-prop-duft]");
  const propDuft1 = root.querySelector("[data-prop-duft-1]");
  const propDuft2 = root.querySelector("[data-prop-duft-2]");

  const duftBeschreibungen = Array.from(root.querySelectorAll("[data-duft-beschreibung]"));
  const duftCaption = root.querySelector("[data-duft-caption]");

  function currentMenge() {
    return root.querySelector('[data-option-index="0"]:checked')?.value ?? variants[0].options[0];
  }
  function isDouble() { return currentMenge() === doubleValue; }
  function currentDuft1() { return root.querySelector('[name="duft1"]:checked')?.value ?? ""; }
  function currentDuft2() { return root.querySelector('[name="duft2"]:checked')?.value ?? ""; }

  // Property-Inputs setzen + je nach Menge deaktivieren (deaktivierte Felder submitten nicht).
  // buildDuftProperties bestimmt die Keys; hier auf die realen Inputs abgebildet.
  function syncDuftProps() {
    const dbl = isDouble();
    const props = buildDuftProperties(dbl, currentDuft1(), currentDuft2());
    if (propDuft)  { propDuft.value = props["Duft"] ?? ""; propDuft.disabled = dbl; }
    if (propDuft1) { propDuft1.value = props["Duft 1"] ?? ""; propDuft1.disabled = !dbl; }
    if (propDuft2) { propDuft2.value = props["Duft 2"] ?? ""; propDuft2.disabled = !dbl; }
    if (duftGroup2) duftGroup2.hidden = !dbl;
  }

  // Beschreibung + Bildunterschrift auf den ersten Duft (Duft 1) schalten.
  function syncDuftInfo() {
    const d1 = currentDuft1();
    duftBeschreibungen.forEach(el => { el.hidden = el.dataset.duftName !== d1; });
    if (duftCaption && d1) duftCaption.textContent = "Duft: " + d1;
  }

  function syncVariant() {
    const variant = selectVariant(variants, [currentMenge()]);
    if (!variant) return;
    if (idField) idField.value = variant.id;
    const priceState = buildPriceState(variant.price, variant.compareAt);
    const currentPrice = priceDisplay?.querySelector("[data-price-current]");
    const comparePrice = priceDisplay?.querySelector("[data-price-compare]");
    const priceBadge = priceDisplay?.querySelector("[data-price-badge]");
    if (currentPrice) currentPrice.textContent = priceState.current;
    if (comparePrice) { comparePrice.textContent = priceState.compare; comparePrice.hidden = !priceState.onSale; }
    if (priceBadge) { priceBadge.textContent = priceState.onSale ? `-${priceState.percent}%` : ""; priceBadge.hidden = !priceState.onSale; }
    if (addBtn) {
      addBtn.disabled = !variant.available;
      addBtn.textContent = variant.available ? `In den Warenkorb · ${formatEuro(variant.price)}` : "Ausverkauft";
    }
  }

  function sync() {
    syncDuftProps();
    syncDuftInfo();
    syncVariant();
  }

  // ── Galerie: Track, Punkte, Thumbnails ─────────────────────────────────
  const track = root.querySelector("[data-gallery-track]");
  const slides = track ? Array.from(track.querySelectorAll(".gallery-slide")) : [];
  const dots = Array.from(root.querySelectorAll("[data-dot]"));
  const photoThumbs = Array.from(root.querySelectorAll("[data-duft-thumb]"));

  function markActive(index) {
    dots.forEach((d, i) => {
      const on = i === index;
      d.classList.toggle("gallery-dot--active", on);
      d.setAttribute("aria-pressed", on ? "true" : "false");
    });
    photoThumbs.forEach((t, i) => {
      const on = i === index;
      t.classList.toggle("product-thumbnail--active", on);
      t.setAttribute("aria-pressed", on ? "true" : "false");
    });
  }
  function goToSlide(index) {
    if (!track || !slides.length) return;
    const i = Math.min(slides.length - 1, Math.max(0, index));
    track.scrollTo({ left: i * track.clientWidth, behavior: "smooth" });
    markActive(i);
  }
  let scrollRaf = 0;
  if (track) {
    track.addEventListener("scroll", () => {
      if (scrollRaf) return;
      scrollRaf = requestAnimationFrame(() => {
        scrollRaf = 0;
        markActive(activeSlideIndex(track.scrollLeft, track.clientWidth, slides.length));
      });
    });
  }
  [...photoThumbs, ...dots].forEach(btn => {
    btn.addEventListener("click", () => { goToSlide(Number(btn.dataset.index) || 0); });
  });

  root.addEventListener("input", sync);
  root.addEventListener("change", sync);
  markActive(0);
  sync();
}

if (typeof document !== "undefined") {
  document.querySelectorAll("[data-nachfuellset-configurator]").forEach(initNachfuellset);
}
